import {showNotification} from "@/actions";
import {parse as parseYaml} from "yaml";
import FileSelector from "@/components/FileSelector";
import FormattedButton from "@/components/FormattedButton";
import {Link} from "@/components/widgets";
import {
    DialogActions,
    DialogContent,
    FormattedDialogTitle,
    FormDialogActionButtons,
    useErrorMessages,
    useHookClass,
} from "@dvrt/graphql-widgets";
import {Box, Card, CircularProgress, Dialog, LinearProgress, Typography, useTheme} from "@mui/material";
import {CSSProperties, useMemo, useState} from "react";
import {FormattedMessage, MessageDescriptor, defineMessage} from "react-intl";
import Mutator from "./mutator";
import {Grid} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useDispatch} from "react-redux";
import {useDeleteDomainSubscription} from "../delete/mutations.generated";
import {parseContext} from "@/utils/ErrorHelper";
import {Error as MutationError} from "@/api";
import {DomainExportYaml} from "../export/types";
import {Stage} from "./utils";
import {msg_import} from "@/utils/translations";
import ThemedFontAwesomeIcon from "@/components/ThemedFontAwesomeIcon";

type Props = {
    refetch: () => void;
};

const successNotification = (id: number, name: string) =>
    showNotification(
        defineMessage({id: "domainCreated", defaultMessage: "Domain {link} has been successfully created."}),
        "success",
        {
            link: <Link to={`/domains/${id}`}>{name}</Link>,
        },
        10000,
    );

const loadJSZip = async () => import(/* webpackChunkName: "jszip" */ "jszip").then((module) => module.default);

enum FileLoadingStage {
    IDLE,
    LOADING,
    SUCCESS,
    ERROR,
}

type FileHandlingState = {
    stage: FileLoadingStage;
    file: File | null;
    errorTranslationDesc: MessageDescriptor | null;
};

const LoadingWrapper = ({loading, children}: {loading: boolean; children: React.ReactNode}) => {
    if (loading) {
        // TODO: 25 as size is a perfect fit for this element, but is not the ideal way
        // to set the size of the spinner.
        return <CircularProgress size={25} />;
    }

    return children;
};

const FileError = ({errorDesc}: {errorDesc: MessageDescriptor}) => {
    const theme = useTheme();

    const backgroundColor = theme.palette.error.main;
    const color = theme.palette.error.contrastText;

    return (
        <Card variant="outlined" sx={{backgroundColor, padding: "1rem"}}>
            <p style={{color, margin: 0}}>
                <FormattedMessage {...errorDesc} />
            </p>
        </Card>
    );
};

const ImportDialog = ({open, onClose, refetch}: {open: boolean; onClose: () => void; refetch: () => void}) => {
    const [running, setRunning] = useState(false);
    const [fileHandlingState, setFileHandlingState] = useState<FileHandlingState>({
        stage: FileLoadingStage.IDLE,
        file: null,
        errorTranslationDesc: null,
    });

    const updateFileHandlingState = (newState: Partial<FileHandlingState>) => {
        setFileHandlingState((prev) => ({
            ...prev,
            ...newState,
        }));
    };

    const mediaGetter = async (mediaId: string): Promise<File> => {
        const JSZip = await loadJSZip();
        const zipFile = await JSZip.loadAsync(fileHandlingState.file);
        const mediaFilename = mediaId.endsWith(".wav") ? mediaId : mediaId + ".wav";
        const mediaFileObject = zipFile.file(mediaFilename);
        const mediaContent = await mediaFileObject.async("blob");
        const mediaFile = new File([mediaContent], mediaId);
        return mediaFile;
    };

    const mutator = useHookClass(Mutator, mediaGetter, successNotification);

    const load = async (file: File) => {
        updateFileHandlingState({stage: FileLoadingStage.LOADING, file, errorTranslationDesc: null});
        const JSZip = await loadJSZip();

        let zipFile: typeof JSZip;
        try {
            zipFile = await JSZip.loadAsync(file);
        } catch {
            updateFileHandlingState({
                stage: FileLoadingStage.ERROR,
                errorTranslationDesc: defineMessage({
                    id: "domain_import.errors.invalid_zip",
                    defaultMessage: "The uploaded file is not a valid zip file.",
                }),
            });
            return;
        }

        const yamlFile = zipFile.file("domain.yaml");
        if (!yamlFile) {
            updateFileHandlingState({
                stage: FileLoadingStage.ERROR,
                errorTranslationDesc: defineMessage({
                    id: "domain_import.errors.no_domain_yaml",
                    defaultMessage: "No domain.yaml file found in zip file.",
                }),
            });
            return;
        }

        const yamlContent = await yamlFile.async("string");

        let parsedData: DomainExportYaml;
        try {
            parsedData = parseYaml(yamlContent);
        } catch {
            updateFileHandlingState({
                stage: FileLoadingStage.ERROR,
                errorTranslationDesc: defineMessage({
                    id: "domain_import.errors.invalid_yaml",
                    defaultMessage: "The domain.yaml file is not a valid YAML file.",
                }),
            });
            return;
        }

        mutator.setData(parsedData);
        updateFileHandlingState({stage: FileLoadingStage.SUCCESS});
    };

    const handleCancel = () => {
        if (running) {
            mutator.cancel();
        } else {
            updateFileHandlingState({stage: FileLoadingStage.IDLE, errorTranslationDesc: null});
            mutator.reset();
            refetch();
            onClose();
        }
    };

    const handleRun = async () => {
        if (fileHandlingState.file && fileHandlingState.stage === FileLoadingStage.SUCCESS && !running) {
            setRunning(true);
            await mutator.runAllStages();
            if (mutator.cancelled) {
                setRunning(false);
                return;
            }

            refetch();
            setRunning(false);

            if (mutator.errors.errors.length === 0) {
                handleCancel();
            }
        }
    };

    const handleReset = () => {
        mutator.reset();
        updateFileHandlingState({stage: FileLoadingStage.IDLE, file: null});
    };

    const ErrorsForCurrentStage = () => {
        const errorDict = mutator.errors;

        if (errorDict.errors.length > 0 || mutator.cancelled)
            return (
                <div>
                    <p>{errorDict.stage}</p>
                    <MutationErrorItem errors={errorDict.errors} />
                    <PartialImportWarning
                        domainId={mutator.domainId}
                        onDeleted={() => mutator.setDomainId(undefined)}
                        disabled={running}
                    />
                </div>
            );
        else return null;
    };

    return (
        <Dialog open={open} fullWidth maxWidth="md">
            <FormattedDialogTitle title={defineMessage({id: "domain_import.title", defaultMessage: "Domain import"})} />
            <DialogContent>
                <Grid container spacing={2} direction="column">
                    <Grid>
                        <FileSelector
                            acceptFileType=".zip"
                            acceptMultiple={false}
                            onChange={load}
                            variant="contained"
                            color="primary"
                            disabled={running || fileHandlingState.stage === FileLoadingStage.LOADING}
                        >
                            <LoadingWrapper loading={fileHandlingState.stage === FileLoadingStage.LOADING}>
                                {fileHandlingState.file !== null ? fileHandlingState.file.name : "Select file"}
                            </LoadingWrapper>
                        </FileSelector>
                    </Grid>
                    {fileHandlingState.errorTranslationDesc !== null && (
                        <Grid>
                            <FileError errorDesc={fileHandlingState.errorTranslationDesc} />
                        </Grid>
                    )}
                    {running && (
                        <Grid>
                            <Grid container spacing={1} direction="column">
                                <Grid>
                                    <StageProgressCarousel stages={mutator.foreshadowedStates} />
                                </Grid>
                                <Grid>
                                    <LinearProgress variant="determinate" value={mutator.progress} color="primary" />
                                </Grid>
                                <Grid>
                                    <p>{mutator.state}</p>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                    <Grid>
                        <ErrorsForCurrentStage />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <FormDialogActionButtons
                    mainAction={handleRun}
                    secondaryAction={handleReset}
                    cancel={handleCancel}
                    mainButtonEnabled={fileHandlingState.file !== null}
                    enabled={!running && fileHandlingState.stage === FileLoadingStage.SUCCESS}
                    mainButtonText={msg_import}
                />
            </DialogActions>
        </Dialog>
    );
};

const DomainImport = ({refetch}: Props) => {
    const [open, setOpen] = useState(false);

    const onClose = () => {
        setOpen(false);
    };

    return (
        <>
            <FormattedButton
                onClick={() => setOpen(true)}
                message={defineMessage({id: "import", defaultMessage: "Import"})}
            />
            <ImportDialog open={open} onClose={onClose} refetch={refetch} />
        </>
    );
};

const MutationErrorItem = ({errors}: {errors: MutationError[]}) => {
    const errorMessages = useErrorMessages();

    return (
        <ul>
            {errors.map((error, index) => {
                let errorTranslation = errorMessages[error.type];

                if (!errorTranslation) {
                    console.error(`No translation found for error type: ${error.type}`);
                    errorTranslation = errorMessages["value_error.unknown"];
                }

                return (
                    <li key={`mutation_error_${index}`}>
                        <h4>
                            <FormattedMessage {...errorTranslation} values={parseContext(error.context)} />
                        </h4>
                        <p>{error.location?.join(" -> ")}</p>
                    </li>
                );
            })}
        </ul>
    );
};

type DeletionStages = "initial" | "deleting" | "deleted";

const PartialImportWarning = ({
    domainId,
    onDeleted,
    disabled,
}: {
    domainId: number | undefined;
    onDeleted: () => void;
    disabled: boolean;
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [deletionStage, setDeletionStage] = useState<DeletionStages>("initial");
    const {data: deleteDomainData} = useDeleteDomainSubscription({
        skip: deletionStage !== "deleting",
        variables: {id: domainId},
        onComplete: () => {
            setDeletionStage("deleted");
        },
        onData: ({data}) => {
            if (data.data?.deleteDomain.errors) {
                dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            } else if (data.data?.deleteDomain.progress === 1) {
                dispatch(
                    showNotification(
                        defineMessage({
                            id: "domainDeleted",
                            defaultMessage: "Domain has been successfully deleted.",
                        }),
                        "success",
                    ),
                );
                onDeleted();
            }
        },
    });

    const deleteButtonVisible = domainId && deletionStage !== "deleted";

    const backgroundColor = theme.palette.warning.main;
    const color = theme.palette.warning.contrastText;
    const paragraphStyle: CSSProperties = deleteButtonVisible
        ? {
              color,
              marginTop: 0,
          }
        : {
              color,
              margin: 0,
          };

    return (
        <Card variant="outlined" sx={{backgroundColor, padding: "1rem"}}>
            <p style={paragraphStyle}>
                <FormattedMessage
                    id="domain_import.partial_import_warning"
                    defaultMessage="Only a part of the data has been imported. Please resolve the issues above, delete the partially imported domain and try again."
                />
            </p>
            {deleteButtonVisible && (
                <FormattedButton
                    message={defineMessage({id: "domain", defaultMessage: "Domain"})}
                    variant="contained"
                    color="secondary"
                    startIcon={<FontAwesomeIcon icon="trash" />}
                    onClick={() => setDeletionStage("deleting")}
                    pending={deletionStage === "deleting"}
                    progress={deleteDomainData?.deleteDomain.progress * 100}
                    disabled={disabled}
                />
            )}
        </Card>
    );
};

const StageProgressCarousel = ({stages}: {stages: Stage[]}) => {
    const theme = useTheme();

    const stageComponents = useMemo(
        () =>
            stages.map((stage, index) => {
                const key = `stage_${index}`;
                if (index === 0) {
                    return (
                        <Typography key={key}>
                            <FormattedMessage {...stage.label} />
                        </Typography>
                    );
                } else {
                    const color = theme.palette.grey[(400 - index * 100) as 100];
                    return (
                        <Box display="flex" gap={2} alignItems="center" key={key}>
                            <ThemedFontAwesomeIcon icon="angle-right" color={color} />
                            <Typography style={{color}}>
                                <FormattedMessage {...stage.label} />
                            </Typography>
                        </Box>
                    );
                }
            }),
        [stages, theme.palette.grey],
    );

    return (
        <Box display="flex" gap={2}>
            {stageComponents}
        </Box>
    );
};

export default DomainImport;
